
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

export interface EnhancedLead {
  id: string;
  nursing_home_name: string;
  nursing_home_id: string;
  location?: string | null;
  status?: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  notes?: string | null;
  is_public: boolean;
  bed_availability: number;
  fair_deal: boolean;
  price_range?: string | null;
  category?: string;
}

interface NursingHomeData {
  nh_slug: string;
  category?: string | null;
  price_range?: string | null;
}

interface WaitlistData {
  id: string;
  status: string;
  created_at: string;
  preferred_location: string[];
}

export function useDashboardData(user: User | null, refreshTrigger = 0) {
  const [nursingHomeLeads, setNursingHomeLeads] = useState<EnhancedLead[]>([]);
  const [waitlistData, setWaitlistData] = useState<WaitlistData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      const { data: leadsData, error: leadsError } = await supabase
        .from('nursing_home_leads')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (leadsError) {
        console.error('Error fetching leads:', leadsError);
        throw leadsError;
      }

      if (leadsData) {
        console.log('Fetched leads data:', leadsData);
      } else {
        console.log('No leads data returned');
      }

      const nursingHomeIds = leadsData?.map(lead => lead.nursing_home_id) || [];
      
      let nursingHomesData: NursingHomeData[] = [];
      if (nursingHomeIds.length > 0) {
        const { data: nhData, error: nhError } = await supabase
          .from('nursing_homes')
          .select('nh_slug, category, price_range')
          .in('nh_slug', nursingHomeIds);
          
        if (nhError) {
          console.error('Error fetching nursing homes:', nhError);
        } else if (nhData) {
          nursingHomesData = nhData;
          console.log('Fetched nursing homes data:', nhData);
        }
      }
      
      const nursingHomesMap = new Map<string, NursingHomeData>();
      nursingHomesData.forEach(nh => {
        nursingHomesMap.set(nh.nh_slug, nh);
      });
      
      const processedLeads = leadsData?.map((lead) => {
        const nhData = nursingHomesMap.get(lead.nursing_home_id);
        
        const enhancedLead: EnhancedLead = {
          ...lead,
          category: nhData?.category || 'Unknown',
          is_public: nhData?.category === 'Public',
          bed_availability: 0,
          fair_deal: false,
          price_range: nhData?.price_range || null,
        };
        
        return enhancedLead;
      }) || [];

      const uniqueLeads: EnhancedLead[] = [];
      const leadIds = new Set();
      
      processedLeads.forEach((lead: EnhancedLead) => {
        if (!leadIds.has(lead.nursing_home_id)) {
          leadIds.add(lead.nursing_home_id);
          uniqueLeads.push(lead);
        }
      });
      
      setNursingHomeLeads(uniqueLeads);
      
      const { data: waitlistData, error: waitlistError } = await supabase
        .from('waitlist_submissions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (waitlistError) throw waitlistError;
      
      if (waitlistData) {
        setWaitlistData(waitlistData);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Refetch function that can be called from outside
  const refetch = () => {
    fetchData();
  };
  
  useEffect(() => {
    fetchData();
  }, [user, refreshTrigger]); // Add refreshTrigger as a dependency
  
  return { nursingHomeLeads, waitlistData, isLoading, refetch };
}
